/**
 * SubscriptionsScreen.jsx
 * Abo-Verwaltung für Mobile App mit Kündigungs-Option
 */
import React, { useState, useEffect } from 'react';
import { 
  Repeat, Package, RefreshCw, Clock, AlertTriangle, Check,
  X, ChevronRight, Calendar, CreditCard, Ban
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const SubscriptionsScreen = ({ portalToken, customerId, onNavigate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (portalToken) loadSubscriptions();
  }, [portalToken]);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/subscriptions`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setSubscriptions(response.data?.subscriptions || []);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const openCancelModal = (sub) => {
    setSelectedSub(sub);
    setShowCancelModal(true);
  };

  const cancelSubscription = async () => {
    if (!selectedSub) return;
    
    setCancelling(true);
    try {
      const response = await axios.post(
        `${API}/portal/subscriptions/${selectedSub.id}/cancel`,
        {},
        { headers: { Authorization: `Bearer ${portalToken}` } }
      );
      
      toast.success(response.data.message || 'Kündigung beantragt');
      setShowCancelModal(false);
      setSelectedSub(null);
      loadSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Kündigen');
    } finally {
      setCancelling(false);
    }
  };

  const activeRecurring = subscriptions.filter(s => s.is_recurring && s.status === 'active');
  const activeCredits = subscriptions.filter(s => !s.is_recurring && s.status === 'active');
  const cancelled = subscriptions.filter(s => s.status === 'cancelled' || s.cancellation_requested);

  return (
    <div className="p-4 space-y-4 pb-24" data-testid="subscriptions-screen">
      {/* Header */}
      <div className="flex justify-between items-center pt-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/30 to-pink-500/20 flex items-center justify-center">
            <Repeat className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h1 className="text-white text-base font-bold">Meine Abos</h1>
            <p className="text-zinc-400 text-xs">Pakete & Abonnements</p>
          </div>
        </div>
        <button 
          onClick={loadSubscriptions}
          disabled={loading}
          className="p-2 bg-zinc-800 rounded-lg"
        >
          <RefreshCw className={`w-5 h-5 text-zinc-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">Lade Abos...</p>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="bg-zinc-800/50 rounded-2xl p-8 text-center">
          <Repeat className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400">Keine aktiven Abos</p>
          <button 
            onClick={() => onNavigate?.('shop')}
            className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-lg text-sm font-medium"
          >
            Zum Shop
          </button>
        </div>
      ) : (
        <>
          {/* Recurring Subscriptions */}
          {activeRecurring.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                <Repeat className="w-4 h-4 text-green-400" />
                Wiederkehrende Abos
              </h2>
              
              {/* Info Banner */}
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 shrink-0" />
                <p className="text-orange-300 text-xs">
                  Wiederkehrende Abos haben eine Mindestlaufzeit von 6 Monaten.
                </p>
              </div>

              {activeRecurring.map((sub) => (
                <div 
                  key={sub.id}
                  className="bg-zinc-800/50 rounded-xl p-4 border border-green-500/20"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                        <Repeat className="w-6 h-6 text-green-400" />
                      </div>
                      <div>
                        <p className="text-white font-bold">{sub.name}</p>
                        <p className="text-zinc-400 text-sm">
                          {sub.points_per_period || sub.credits} {sub.points_per_period ? 'Punkte' : 'Einheiten'}/Monat
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                      Aktiv
                    </span>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between text-zinc-400">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Gekauft
                      </span>
                      <span className="text-white">{formatDate(sub.purchase_date || sub.created_at)}</span>
                    </div>
                    {sub.next_billing_date && (
                      <div className="flex items-center justify-between text-zinc-400">
                        <span className="flex items-center gap-1">
                          <CreditCard className="w-3 h-3" /> Nächste Abbuchung
                        </span>
                        <span className="text-white">{formatDate(sub.next_billing_date)}</span>
                      </div>
                    )}
                  </div>

                  {/* Cancellation UI */}
                  <div className="mt-4 pt-3 border-t border-zinc-700">
                    {sub.cancellation_requested ? (
                      <div className="flex items-center gap-2 text-orange-400 bg-orange-500/10 px-3 py-2 rounded-lg">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">
                          Kündigung zum {formatDate(sub.cancellation_date)} beantragt
                        </span>
                      </div>
                    ) : (
                      <button
                        onClick={() => openCancelModal(sub)}
                        className="w-full py-2 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                        data-testid={`cancel-sub-${sub.id}`}
                      >
                        <Ban className="w-4 h-4" />
                        Abo kündigen
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Credit Packages (10er-Abos) */}
          {activeCredits.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-400" />
                10er-Abos / Punktepakete
              </h2>
              
              {activeCredits.map((sub) => (
                <div 
                  key={sub.id}
                  className="bg-zinc-800/50 rounded-xl p-4 border border-blue-500/20"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                        <Package className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-bold">{sub.name}</p>
                        <p className="text-zinc-400 text-sm">
                          {sub.remaining_credits || sub.credits} / {sub.credits} übrig
                        </p>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                      {sub.remaining_credits || sub.credits} übrig
                    </span>
                  </div>

                  {sub.valid_until && (
                    <div className="mt-3 pt-3 border-t border-zinc-700 text-sm text-zinc-400 flex justify-between">
                      <span>Gültig bis:</span>
                      <span className="text-white">{formatDate(sub.valid_until)}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Cancelled / Pending Cancellation */}
          {cancelled.length > 0 && (
            <div className="space-y-3">
              <h2 className="text-white font-semibold text-sm flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-400" />
                Gekündigt / Auslaufend
              </h2>
              
              {cancelled.map((sub) => (
                <div 
                  key={sub.id}
                  className="bg-zinc-800/30 rounded-xl p-4 border border-zinc-700 opacity-70"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-zinc-700 rounded-xl flex items-center justify-center">
                        <X className="w-5 h-5 text-zinc-500" />
                      </div>
                      <div>
                        <p className="text-zinc-400 font-medium">{sub.name}</p>
                        <p className="text-zinc-500 text-xs">
                          {sub.cancellation_date 
                            ? `Endet am ${formatDate(sub.cancellation_date)}`
                            : 'Gekündigt'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Cancel Confirmation Modal */}
      {showCancelModal && selectedSub && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-end justify-center">
          <div className="bg-zinc-900 rounded-t-3xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white font-bold text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Abo kündigen
              </h2>
              <button onClick={() => setShowCancelModal(false)} className="p-2">
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            
            <p className="text-zinc-300 mb-4">
              Möchtest du <strong>"{selectedSub.name}"</strong> wirklich kündigen?
            </p>
            
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-orange-400 mt-0.5" />
                <div className="text-sm text-orange-200">
                  <p className="font-medium mb-1">Mindestlaufzeit beachten:</p>
                  <p>Wiederkehrende Abos haben eine Mindestlaufzeit von 6 Monaten. Die Kündigung wird zum Ende der Mindestlaufzeit wirksam. Du kannst dein Abo bis dahin weiter nutzen.</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 py-3 bg-zinc-800 text-white rounded-xl font-medium"
              >
                Abbrechen
              </button>
              <button
                onClick={cancelSubscription}
                disabled={cancelling}
                className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium flex items-center justify-center gap-2"
                data-testid="confirm-cancel-btn"
              >
                {cancelling ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  'Kündigung beantragen'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionsScreen;
